import { getWorkspaceSession, setWorkspaceSession, setWorkspaceRevision, isWorkspaceRequest } from "./lib/workspaceSession";
import { WorkspaceProvider, useWorkspaces } from "./contexts/WorkspaceContext";
import WorkspacesPage from "./pages/WorkspacesPage";
import WorkspaceInvitationPage from "./pages/WorkspaceInvitationPage";
import React, { useState, useEffect, createContext, useContext, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "@/lib/toast";
import { AorraToastProvider } from "./components/AorraToast";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { clearAuthSession, getAccessToken, setAuthSession } from "./lib/authSession";
import MetaPixelTracking from "./components/MetaPixelTracking";

import { gamesPracticeEnabled } from "./features/games/access";

// Context
const AuthContext = createContext(null);
const ThemeContext = createContext(null);

// API Setup
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ 
  baseURL: API,
  withCredentials: true,
  timeout: 30000 // 30 seconds timeout to accommodate cold boots and db health checks
});

const sessionApi = axios.create({ baseURL: API, withCredentials: true, timeout: 30000 });
let refreshPromise = null;

const refreshBrowserSession = async () => {
  if (!refreshPromise) {
    refreshPromise = sessionApi.post("/auth/session/refresh")
      .then(({ data }) => {
        setAuthSession(data.access_token, data.session);
        return data;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

api.interceptors.request.use(async (config) => {
  const token = getAccessToken() || localStorage.getItem("adminToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isWorkspaceRequest(config.url)) {
    const workspace = getWorkspaceSession();
    config.workspaceGeneration = workspace.generation;
    if (workspace.id) {
      config.headers['X-Workspace-ID'] = workspace.id;
      config.workspaceId = workspace.id;
      config.workspaceWrite = !['get', 'head', 'options'].includes((config.method || 'get').toLowerCase());
      if (config.workspaceWrite) {
        if (workspace.revision === null) {
          const result = await api.get(`/workspaces/${workspace.id}/revision`);
          if (getWorkspaceSession().generation !== workspace.generation) throw new axios.CanceledError('Cambió el tablero activo.');
          setWorkspaceRevision(workspace.id, result.data.revision);
        }
        config.headers['If-Match'] = String(getWorkspaceSession().revision);
      }
    }
  }
  return config;
});

// Handle 401 errors globally - force logout when session is invalid
api.interceptors.response.use(
  (response) => {
    if (response.config.workspaceGeneration !== undefined && response.config.workspaceGeneration !== getWorkspaceSession().generation)
      return Promise.reject(new axios.CanceledError('Cambió el tablero activo.'));
    if (response.config.workspaceId) setWorkspaceRevision(response.config.workspaceId, response.headers['x-workspace-revision'], response.config.workspaceWrite);
    if (!["get", "head", "options"].includes((response.config.method || "get").toLowerCase()))
      window.dispatchEvent(new CustomEvent("aorra:quota-updated"));
    return response;
  },
  async (error) => {
    if (error.config?.workspaceGeneration !== undefined && error.config.workspaceGeneration !== getWorkspaceSession().generation)
      return Promise.reject(new axios.CanceledError('Cambió el tablero activo.'));
    if (['workspace_stale', 'workspace_revision_required'].includes(error.response?.data?.detail?.code)) {
      window.dispatchEvent(new CustomEvent('aorra:workspace-conflict', { detail: error.config?.workspaceId }));
    }
    // Older forms expect a string detail; keep their error rendering safe.
    if (error.response?.data?.detail?.code?.startsWith('workspace_')) {
      error.workspaceCode = error.response.data.detail.code;
      error.response.data.detail = error.response.data.detail.message;
    }
    if (error.response?.status === 401) {
      const original = error.config || {};
      const path = String(original.url || "");
      const refreshable = !original._authRetried && ![
        "/auth/session/refresh", "/auth/session/logout", "/auth/session/migrate",
        "/auth/send-otp", "/auth/verify-otp", "/auth/channel-login/exchange", "/auth/admin-login",
      ].some(endpoint => path.includes(endpoint));
      if (refreshable) {
        original._authRetried = true;
        try {
          await refreshBrowserSession();
          return api.request(original);
        } catch (_refreshError) {
          // The shared cleanup below handles an expired or revoked session.
        }
      }
      clearAuthSession();
      localStorage.removeItem("token");
      localStorage.removeItem("adminToken");
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/admin')) {
        toast.error("Sesión expirada. Por favor inicia sesión de nuevo.");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth Provider with Auto-Logout
function WorkspacePortalRoute({ children }) {
  const { appSettings } = useAuth();
  return appSettings?.hidden_modules?.includes('tableros') ? <Navigate to="/dashboard" replace /> : children;
}

function GamesPortalRoute({ children }) {
  const { appSettings } = useAuth();
  if (!gamesPracticeEnabled(appSettings)) return <Navigate to="/dashboard" replace />;
  return <Suspense fallback={<main role="status">Preparando el juego…</main>}>{children}</Suspense>;
}
const VerticalGamePage = lazy(() => import('./features/games/VerticalGamePage'));

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [appSettings, setAppSettings] = useState({ hidden_modules: [], streaks_enabled: false });
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const logout = useCallback(async () => {
    if (user?.role !== 'admin') {
      try { await sessionApi.post("/auth/session/logout"); } catch (_error) { /* Always clear locally. */ }
    }
    setWorkspaceSession('');
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    clearAuthSession();
    setUser(null);
    setSession(null);
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, [user?.role]);

  // Setup auto-logout timer
  useEffect(() => {
    // Only run for regular users (not admin)
    if (!user || user.role === 'admin' || session?.trusted) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const startTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      const remaining = Math.max(0, INACTIVITY_TIMEOUT - (Date.now() - lastActivityRef.current));
      inactivityTimerRef.current = setTimeout(async () => {
        toast.warning("Sesión cerrada por inactividad", {
          description: "Por tu seguridad, cerramos tu sesión después de 10 minutos de inactividad."
        });
        await logout();
        window.location.href = "/login";
      }, remaining);
    };

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      startTimer();
    };

    const verifyElapsedTime = async () => {
      if (Date.now() - lastActivityRef.current >= INACTIVITY_TIMEOUT) {
        toast.warning("Sesión cerrada por inactividad", {
          description: "Por tu seguridad, cerramos tu sesión después de 10 minutos de inactividad."
        });
        await logout();
        window.location.href = "/login";
      } else startTimer();
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    window.addEventListener('focus', verifyElapsedTime);
    document.addEventListener('visibilitychange', verifyElapsedTime);

    // Start initial timer
    lastActivityRef.current = Date.now();
    startTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('focus', verifyElapsedTime);
      document.removeEventListener('visibilitychange', verifyElapsedTime);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [user, session?.trusted, logout]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch public app settings first
        const settingsRes = await api.get("/public/app-settings");
        setAppSettings(settingsRes.data);
      } catch (err) {
        console.error("Error fetching app settings:", err);
      }
      
      const legacyToken = localStorage.getItem("token");
      const adminToken = localStorage.getItem("adminToken");
      if (legacyToken) {
        setAuthSession(legacyToken);
        try {
          const { data } = await api.post("/auth/session/migrate");
          setAuthSession(data.access_token, data.session);
          if (data.user?.role === 'admin') localStorage.setItem("adminToken", data.access_token);
          setUser(data.user);
          setSession(data.session || null);
        } catch (err) {
          clearAuthSession();
        }
        localStorage.removeItem("token");
      } else if (adminToken) {
        setAuthSession(adminToken);
        try {
          const res = await api.get("/auth/me");
          setUser(res.data);
        } catch (_error) {
          localStorage.removeItem("adminToken");
          clearAuthSession();
        }
      } else {
        try {
          const data = await refreshBrowserSession();
          setUser(data.user);
          setSession(data.session || null);
        } catch (_error) {
          clearAuthSession();
        }
      }
      setLoading(false);
    };
    
    fetchInitialData();
  }, []);

  // Settings are changed from a different (administrator) session. Refresh on
  // focus and periodically so a newly enabled module appears without making a
  // user sign out or hard-refresh the dashboard.
  useEffect(() => {
    let mounted = true;
    const refreshAppSettings = async () => {
      try {
        const response = await api.get("/public/app-settings", {
          params: { _refresh: Date.now() },
        });
        if (mounted) setAppSettings(response.data);
      } catch (err) {
        console.error("Error refreshing app settings:", err);
      }
    };
    refreshAppSettings();
    const interval = window.setInterval(refreshAppSettings, 30000);
    window.addEventListener("focus", refreshAppSettings);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshAppSettings);
    };
  }, [user?.id]);

  const login = (token, userData, sessionData = null) => {
    setAuthSession(token, sessionData);
    localStorage.removeItem("token");
    if (userData?.role === 'admin') localStorage.setItem("adminToken", token);
    else localStorage.removeItem("adminToken");
    setUser(userData);
    setSession(sessionData);
  };

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me", { timeout: 12000 });
    setUser(data);
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, login, logout, loading, appSettings, setAppSettings, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useIdentityAuth = () => useContext(AuthContext);
export const useAuth = () => {
  const auth = useIdentityAuth();
  const workspace = useWorkspaces();
  const currency = workspace?.active?.currency;
  return useMemo(() => currency && auth?.user
    ? { ...auth, user: { ...auth.user, currency } }
    : auth, [auth, currency]);
};

// Detect Android device
const isAndroidDevice = () => /android/i.test(navigator.userAgent);

// Theme Provider with auto mode based on time of day
export const ThemeProvider = ({ children }) => {
  const isAndroid = isAndroidDevice();

  // themeMode can be: "light", "dark", "auto"
  // On Android: always lock to "light"
  const [themeMode, setThemeModeState] = useState(() => {
    if (isAndroid) return "light";
    const saved = localStorage.getItem("themeMode");
    return saved || "light";
  });

  // Wrap setThemeMode to be a no-op on Android
  const setThemeMode = (value) => {
    if (isAndroid) return; // Android is always light — ignore
    setThemeModeState(value);
  };

  // Calculate if it should be dark based on time (6pm-6am = dark)
  const shouldBeDark = () => {
    const hour = new Date().getHours();
    return hour >= 18 || hour < 6;
  };

  // Actual dark mode state (computed from themeMode)
  const [darkMode, setDarkModeState] = useState(() => {
    if (isAndroid) return false; // Android always light
    if (themeMode === "auto") return shouldBeDark();
    return themeMode === "dark";
  });

  // Update darkMode when themeMode changes
  useEffect(() => {
    if (isAndroid) return;
    localStorage.setItem("themeMode", themeMode);
    if (themeMode === "auto") {
      setDarkModeState(shouldBeDark());
    } else {
      setDarkModeState(themeMode === "dark");
    }
  }, [themeMode, isAndroid]);

  // Auto-update every minute when in auto mode
  useEffect(() => {
    if (isAndroid || themeMode !== "auto") return;
    const checkTime = () => setDarkModeState(shouldBeDark());
    checkTime();
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [themeMode, isAndroid]);

  // Apply dark class and color-scheme to document
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (isAndroid || !darkMode) {
      // Always light on Android; remove dark class
      document.documentElement.classList.remove("dark");
      document.documentElement.style.colorScheme = "only light";
      if (metaThemeColor) metaThemeColor.setAttribute("content", "#f8fafc");
    } else {
      document.documentElement.classList.add("dark");
      document.documentElement.style.colorScheme = "dark";
      if (metaThemeColor) metaThemeColor.setAttribute("content", "#0a1e44");
    }
  }, [darkMode, isAndroid]);

  // Wrapper to set theme mode (used by components)
  const setDarkMode = (value) => {
    if (isAndroid) return;
    if (typeof value === "boolean") {
      setThemeMode(value ? "dark" : "light");
    } else {
      setThemeMode(value);
    }
  };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode, themeMode, setThemeMode, isAndroid }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8fafc]">
        <img src="/logo-principal.png" alt="Aorra" className="h-16 mb-6 animate-pulse" />
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-[#04f198] rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          <div className="w-3 h-3 bg-[#04f198] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-3 h-3 bg-[#04f198] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    if (window.location.pathname === '/dashboard/tableros/invitacion' && window.location.hash)
      sessionStorage.setItem('aorra.workspace.invitation', window.location.hash.slice(1));
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Error Boundary Component for WhatsApp browser compatibility
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8fafc] p-6 text-center">
          <img src="/logo-principal.png" alt="Aorra" className="h-16 mb-6" />
          <p className="text-[#475569] mb-6 font-medium">Hubo un problema al cargar la aplicación</p>
          <div className="space-y-3 w-full max-w-xs">
            <button 
              onClick={() => window.location.reload()} 
              className="block w-full bg-[#04f198] text-[#1e2b2d] px-6 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-shadow"
            >
              Reintentar
            </button>
            <a 
              href="/login" 
              className="block w-full bg-white border border-[#cbd5e1] text-[#475569] px-6 py-3 rounded-xl font-bold hover:bg-gray-50 transition-colors"
            >
              Ir al Login
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Lazy load pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import TransactionsPage from "./pages/TransactionsPage";
import DebtsPage from "./pages/DebtsPage";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import SavingsPage from "./pages/SavingsPage";
import AccountsPage from "./pages/AccountsPage";
import AssetsPage from "./pages/AssetsPage";
import SharedExpensesPage from "./pages/SharedExpensesPage";
import PlansPage from "./pages/PlansPage";
import RemindersPage from "./pages/RemindersPage";
import HabitsPage from "./pages/HabitsPage";
import AdminPage from "./pages/AdminPage";
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));
import ReferralLandingPage from "./pages/ReferralLandingPage";
import PromotionCapture from "./components/PromotionCapture";
import TripsPage from "./pages/TripsPage";
import TripHistoryPage from "./pages/TripHistoryPage";

// Invisible component that polls for new deployments and auto-reloads
function AppVersionChecker() {
  useVersionCheck(5); // check every 5 minutes
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <MetaPixelTracking />
            <AppVersionChecker />
            <AorraToastProvider>
            <PromotionCapture />
            <Routes>
              <Route path="/r/:code" element={<ReferralLandingPage />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <WorkspaceProvider><DashboardLayout /></WorkspaceProvider>
                  </ProtectedRoute>
                }
              >
              <Route index element={<DashboardPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="tableros" element={<WorkspacePortalRoute><WorkspacesPage /></WorkspacePortalRoute>} />
              <Route path="tableros/invitacion" element={<WorkspacePortalRoute><WorkspaceInvitationPage /></WorkspacePortalRoute>} />
              <Route path="reminders" element={<RemindersPage />} />
              <Route path="juegos" element={<GamesPortalRoute><Navigate to="/dashboard/juegos/salto" replace /></GamesPortalRoute>} />
              <Route path="juegos/salto" element={<GamesPortalRoute><VerticalGamePage /></GamesPortalRoute>} />
              <Route path="habits" element={<HabitsPage />} />
              <Route path="debts" element={<DebtsPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="savings" element={<SavingsPage />} />
              <Route path="credit-cards" element={<AccountsPage />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="shared" element={<SharedExpensesPage />} />
              <Route path="planes" element={<PlansPage />} />
              <Route path="trips" element={<TripsPage />} />
              <Route path="trips/:tripId/history" element={<TripHistoryPage />} />
            </Route>
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute adminOnly>
                  <Suspense fallback={<main className="p-6" role="status">Cargando estadísticas…</main>}>
                    <AdminAnalyticsPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
          </Routes>
          </AorraToastProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export { api, API };
export default App;
